"use client";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import { ASSETS, HERO } from "./data";
import { VsGrainOverlay } from "./grain-overlay";
import { VsHeroVideo } from "./hero-video";

interface VsHeroProps {
  className?: string;
}

/**
 * New York announcement hero. A centered single column — eyebrow, headline,
 * subhead, CTAs — sitting low over the VISIONARIES wordmark / video / grain
 * background. The bottom darken keeps the copy legible over the video.
 */
export function VsHero({ className }: VsHeroProps) {
  return (
    <header
      className={cn("relative w-full overflow-hidden bg-[#3a1660]", className)}
    >
      <VsHeroVideo
        src={ASSETS.heroVideo}
        poster={ASSETS.heroPoster}
        className="opacity-90"
      />
      <VsGrainOverlay />

      {/* VISIONARIES wordmark background — same masked-overlay treatment as the
          original hero, kept as a faint highlight behind the split layout. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute top-[179px] left-1/2 -translate-x-1/2 opacity-30",
          "h-[435px] w-[1378px] max-w-[100vw]",
          "max-md:top-[160px] max-md:aspect-[358/113] max-md:h-auto max-md:w-[95vw]"
        )}
        style={{
          backgroundImage:
            "linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0) 100%)",
          maskImage: `url('${ASSETS.visionariesTitle}')`,
          WebkitMaskImage: `url('${ASSETS.visionariesTitle}')`,
          maskSize: "163.7% 296%",
          WebkitMaskSize: "163.7% 296%",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskPosition: "center center",
          WebkitMaskPosition: "center center",
          mixBlendMode: "overlay",
        }}
      />
      <span className="sr-only">VISIONARIES</span>

      {/* Bottom darken so the foreground copy reads cleanly over the mesh.
          Ramps to solid #0d0d0d earlier/heavier than the mesh so the copy,
          which sits low in the hero, lands on a near-black band. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[180px] bottom-0 z-[1] bg-[linear-gradient(to_bottom,rgba(13,13,13,0)_0%,rgba(13,13,13,0.45)_38%,rgba(13,13,13,0.88)_68%,#0d0d0d_88%)]"
      />

      {/* Foreground — centered single column, sitting low over the fade. */}
      <div
        className={cn(
          "relative z-10 mx-auto flex w-full max-w-[1440px] flex-col items-center justify-end text-center",
          "min-h-[820px] px-8 pt-[260px] pb-28",
          "max-md:min-h-0 max-md:px-6 max-md:pt-[230px] max-md:pb-16"
        )}
      >
        <div
          style={{ fontFeatureSettings: VS_FONT_FEATURES }}
          className="flex max-w-[920px] flex-col items-center text-white"
        >
          <p className="mb-4 text-[13px] leading-[1.4] text-white/80 max-md:mb-3 max-md:text-[12px]">
            {HERO.eyebrow}
          </p>
          <h1
            style={{ fontFamily: "BBHHegarty", fontFeatureSettings: "normal" }}
            className="mb-5 text-[80px] leading-[1.2] font-normal tracking-[-3.2px] whitespace-nowrap uppercase max-md:mb-4 max-md:text-[36px] max-md:tracking-[-1.44px] max-md:whitespace-normal"
          >
            Visionaries Summit
          </h1>
          <p className="mb-8 max-w-[560px] text-[16px] leading-[1.4] font-normal tracking-[-0.2px] text-white/85 max-md:mb-6 max-md:text-[14px]">
            {HERO.subhead}
          </p>

          <div className="flex items-center justify-center gap-2 max-md:w-full max-md:flex-col max-md:items-stretch">
            <a
              href={HERO.primaryCta.href}
              target={
                HERO.primaryCta.href.startsWith("#") ? undefined : "_blank"
              }
              rel={
                HERO.primaryCta.href.startsWith("#")
                  ? undefined
                  : "noopener noreferrer"
              }
              className={cn(
                "inline-flex h-10 items-center justify-center gap-1 rounded-full bg-[#001eff] px-4 text-[16px] leading-none font-medium text-white shadow-[0_4px_1px_-2px_rgba(0,0,0,0.06)]",
                "transition-colors hover:bg-[#1a1aff] motion-reduce:transition-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
              )}
            >
              {HERO.primaryCta.label}
              <span aria-hidden className="text-[18px] leading-none">
                &rarr;
              </span>
            </a>
            <a
              href={HERO.secondaryCta.href}
              className={cn(
                "inline-flex h-10 items-center justify-center gap-1 rounded-full px-3 text-[16px] leading-none font-medium text-white",
                "transition-opacity hover:opacity-80 motion-reduce:transition-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
              )}
            >
              {HERO.secondaryCta.label}
              <span aria-hidden className="text-[18px] leading-none">
                &rarr;
              </span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
